import { Link } from 'react-router'

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-8">
          <Link to="/" className="text-primary-600 hover:underline">
            &larr; Back to Home
          </Link>
        </div>

        <div className="rounded-lg bg-white p-8 shadow">
          <h1 className="mb-6 text-3xl font-bold">Terms of Service</h1>
          <p className="mb-4 text-sm text-gray-500">Last updated: January 2025</p>

          <div className="prose prose-gray max-w-none">
            <h2 className="mt-8 text-xl font-semibold">1. Agreement to Terms</h2>
            <p className="mt-2 text-gray-600">
              By accessing or using Brightseed ("the Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.
            </p>

            <h2 className="mt-8 text-xl font-semibold">2. Description of Service</h2>
            <p className="mt-2 text-gray-600">
              Brightseed is a mobile application that enables childminders to share daily updates about children in their care with parents and guardians. The Service includes:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Child profile management</li>
              <li>Daily update creation and sharing</li>
              <li>Photo sharing</li>
              <li>Push notifications</li>
              <li>Parent invitation system</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">3. User Accounts</h2>
            <p className="mt-2 text-gray-600">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
            <p className="mt-2 text-gray-600">
              You must be at least 18 years old to create an account.
            </p>

            <h2 className="mt-8 text-xl font-semibold">4. User Types</h2>
            <h3 className="mt-4 text-lg font-medium">Childminders</h3>
            <p className="mt-2 text-gray-600">
              Childminder accounts can create child profiles, add daily updates, upload photos, and invite parents to view their child's updates.
            </p>
            <h3 className="mt-4 text-lg font-medium">Parents</h3>
            <p className="mt-2 text-gray-600">
              Parent accounts can view updates for children they have been invited to access by a childminder.
            </p>

            <h2 className="mt-8 text-xl font-semibold">5. Acceptable Use</h2>
            <p className="mt-2 text-gray-600">You agree not to:</p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Upload content that is illegal, harmful, or violates others' rights</li>
              <li>Share inappropriate images of children</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to other accounts</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Impersonate another person or entity</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">6. Content Ownership</h2>
            <h3 className="mt-4 text-lg font-medium">Your Content</h3>
            <p className="mt-2 text-gray-600">
              You retain ownership of content you upload (photos, updates, notes). By uploading content, you grant us a limited license to store, display, and transmit that content as necessary to provide the Service.
            </p>
            <h3 className="mt-4 text-lg font-medium">Our Content</h3>
            <p className="mt-2 text-gray-600">
              The Service and its original content (excluding user content), features, and functionality are owned by Brightseed and are protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h2 className="mt-8 text-xl font-semibold">7. Subscription and Payments</h2>
            <p className="mt-2 text-gray-600">
              Certain features require a paid subscription:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Free tier includes 1 child profile</li>
              <li>Paid subscriptions allow additional child profiles</li>
              <li>Subscriptions are billed monthly</li>
              <li>Payments are processed securely through Stripe</li>
            </ul>
            <p className="mt-2 text-gray-600">
              You may cancel your subscription at any time. Upon cancellation, you retain access until the end of your current billing period, after which excess child profiles may be locked (read-only).
            </p>

            <h2 className="mt-8 text-xl font-semibold">8. Refund Policy</h2>
            <p className="mt-2 text-gray-600">
              Subscription payments are generally non-refundable. However, we may provide refunds at our discretion for issues such as:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Accidental duplicate purchases</li>
              <li>Service unavailability for extended periods</li>
              <li>Technical issues preventing use of paid features</li>
            </ul>

            <h2 className="mt-8 text-xl font-semibold">9. Privacy</h2>
            <p className="mt-2 text-gray-600">
              Your privacy is important to us. Please review our{' '}
              <Link to="/privacy" className="text-primary-600 hover:underline">
                Privacy Policy
              </Link>
              , which explains how we collect, use, and protect your information.
            </p>

            <h2 className="mt-8 text-xl font-semibold">10. Termination</h2>
            <p className="mt-2 text-gray-600">
              We may terminate or suspend your account immediately, without prior notice, for:
            </p>
            <ul className="mt-2 list-disc pl-6 text-gray-600">
              <li>Violation of these Terms</li>
              <li>Conduct harmful to other users or the Service</li>
              <li>At our sole discretion for any reason</li>
            </ul>
            <p className="mt-2 text-gray-600">
              You may delete your account at any time through the Settings page.
            </p>

            <h2 className="mt-8 text-xl font-semibold">11. Disclaimer of Warranties</h2>
            <p className="mt-2 text-gray-600">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>

            <h2 className="mt-8 text-xl font-semibold">12. Limitation of Liability</h2>
            <p className="mt-2 text-gray-600">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BRIGHTSEED SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE.
            </p>

            <h2 className="mt-8 text-xl font-semibold">13. Indemnification</h2>
            <p className="mt-2 text-gray-600">
              You agree to indemnify and hold harmless Brightseed and its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
            </p>

            <h2 className="mt-8 text-xl font-semibold">14. Governing Law</h2>
            <p className="mt-2 text-gray-600">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.
            </p>

            <h2 className="mt-8 text-xl font-semibold">15. Changes to Terms</h2>
            <p className="mt-2 text-gray-600">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes via email or through the app. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>

            <h2 className="mt-8 text-xl font-semibold">16. Contact Us</h2>
            <p className="mt-2 text-gray-600">
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="mt-2 text-gray-600">
              Email: support@brightseed.app
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
